import React, { useState, useEffect } from "react";
import {
  Share2,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Send,
  X,
  Loader2,
  Plus,
} from "lucide-react";
import {
  getCandidateCvCycles,
  createApplicationPresentation,
} from "@/lib/recruitmentApi";
import { getApiErrorMessage } from "@/lib/api";

export default function ClientPresentationModal({
  isOpen,
  onClose,
  applicationId,
  candidateId,
  needId,
  onPresentationCompleted,
}) {
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [clientRecipients, setClientRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState("");
  const [clientCc, setClientCc] = useState([]);
  const [newCc, setNewCc] = useState("");
  const [notes, setNotes] = useState("");
  const [subject, setSubject] = useState("");

  const [isLoadingCycles, setIsLoadingCycles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load candidate's finalized cycles
  useEffect(() => {
    if (!isOpen || !candidateId) return;

    const loadCycles = async () => {
      setIsLoadingCycles(true);
      setError(null);
      try {
        const list = await getCandidateCvCycles(candidateId);
        // Only finalized cycles can be presented
        const finalized = (Array.isArray(list) ? list : []).filter((c) => c.status === "finalized");
        setCycles(finalized);
        if (finalized.length > 0) {
          setSelectedCycleId(finalized[0].cycle_id);
        }
      } catch (err) {
        setError(getApiErrorMessage(err, "Impossible de charger les cycles CV du candidat"));
      } finally {
        setIsLoadingCycles(false);
      }
    };

    loadCycles();
  }, [isOpen, candidateId]);

  if (!isOpen) return null;

  const handleAddRecipient = () => {
    const val = newRecipient.trim().toLowerCase();
    if (val && !clientRecipients.includes(val)) {
      setClientRecipients([...clientRecipients, val]);
      setNewRecipient("");
    }
  };

  const handleAddCc = () => {
    const val = newCc.trim().toLowerCase();
    if (val && !clientCc.includes(val)) {
      setClientCc([...clientCc, val]);
      setNewCc("");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!selectedCycleId) {
      setError("Veuillez sélectionner un CV finalisé certifié.");
      return;
    }
    if (clientRecipients.length === 0) {
      setError("Veuillez renseigner au moins une adresse email client destinataire.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const data = await createApplicationPresentation(applicationId, {
        cycle_id: selectedCycleId,
        client_recipients: clientRecipients,
        client_cc: clientCc.length > 0 ? clientCc : undefined,
        subject: subject.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      if (onPresentationCompleted) onPresentationCompleted(data);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "Échec de la présentation client"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div className="max-w-xl w-full bg-[#14183a] border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-900/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
              <Share2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Présentation du Candidat au Client</h3>
              <p className="text-xs text-slate-400">
                Joint automatiquement le CV finalisé immuable et avance l'étape à 'Présenté'.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Select Finalized CV */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              Version du CV finalisé à présenter (Rétention L4)
            </label>
            {isLoadingCycles ? (
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-700 flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                <span>Chargement des versions certifiées...</span>
              </div>
            ) : cycles.length === 0 ? (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
                ⚠️ Aucun CV finalisé disponible pour ce candidat. Veuillez finaliser un cycle CV avant de le présenter.
              </div>
            ) : (
              <select
                value={selectedCycleId}
                onChange={(e) => setSelectedCycleId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
              >
                {cycles.map((c) => (
                  <option key={c.cycle_id} value={c.cycle_id}>
                    Cycle #{c.cycle_id.substring(0, 8)} — Rév. {c.revision} (Finalisé le{" "}
                    {c.finalized_at ? new Date(c.finalized_at).toLocaleDateString("fr-FR") : "Date inconnue"})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Client Recipients */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              Destinataire(s) Client (À)
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {clientRecipients.map((addr, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 text-xs flex items-center gap-1.5"
                >
                  <span>{addr}</span>
                  <button
                    type="button"
                    onClick={() => setClientRecipients(clientRecipients.filter((_, i) => i !== idx))}
                    className="hover:text-red-400"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddRecipient())}
                placeholder="contact.client@entreprise.com"
                className="flex-1 px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddRecipient}
                className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-medium"
              >
                Ajouter
              </button>
            </div>
          </div>

          {/* Optional Note */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              Note d'accompagnement / Argumentaire
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Nous avons particulièrement apprécié son expertise sur FastAPI et sa disponibilité sous 2 semaines..."
              className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
            />
          </div>

          <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-[11px] text-slate-400 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span>
              La présentation certifiée garantit l'immutabilité du document et archive l'empreinte SHA-256 dans le dossier de candidature.
            </span>
          </div>

          {/* Footer actions */}
          <div className="pt-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting || cycles.length === 0 || clientRecipients.length === 0}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium text-xs flex items-center gap-2 shadow-lg shadow-violet-600/20 disabled:opacity-50 transition-all"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Présenter au client</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
